import React from "react";
import { toast } from "react-toastify";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_list_labor_laws_for_company,
  API_set_labor_law_hidden_for_store,
  API_list_jurisdictions,
  API_update_store_jurisdictions,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import LoadingButton from "@mui/lab/LoadingButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import { PurpleSwitch } from "../../../helpers/helpers";
import { pulseElement } from "../operations_assistant/helpNavigation";

export const laborLawsStoreCardId = (storeId) => `labor-laws-store-${storeId}`;


function LaborLawsDisplay(props) {

  const { company } = props;
  const [stores, setStores] = React.useState([]);
  const [expandedStores, setExpandedStores] = React.useState({});
  const [expandedRegulations, setExpandedRegulations] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerStore, setDrawerStore] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Jurisdiction options from API
  const [jurisdictionOptions, setJurisdictionOptions] = React.useState({
    federal: [],
    states: [],
    counties: [],
    municipalities: [],
  });

  // Selected jurisdiction IDs for drawer form
  const [selectedJurisdictions, setSelectedJurisdictions] = React.useState({
    federal_id: "",
    state_id: "",
    county_id: "",
    municipal_id: "",
  });

  const getRegulationTypeLabel = (type) => {
    const labels = {
      'meal_break': 'Meal Breaks',
      'meal_break_timing': 'Meal Break Timing',
      'rest_break': 'Rest Breaks',
      'rest_break_timing': 'Rest Break Timing',
      'weekly_overtime': 'Weekly Overtime',
      'daily_overtime': 'Daily Overtime',
      'seventh_day_overtime': '7th Day Overtime',
      'shift_separation': 'Shift Separation',
      'advance_schedule_notice': 'Advance Schedule Notice',
      'schedule_change_penalty': 'Schedule Change Penalty',
      'minor_work_hours_14_15': 'Minor Work Hours (14-15)',
      'minor_work_hours_16_17': 'Minor Work Hours (16-17)',
      'predictive_scheduling': 'Predictive Scheduling',
      'split_shift': 'Split Shift / Spread of Hours',
      'teen_hours_restriction': 'Minor Work Hours'
    };
    return labels[type] || type;
  };

  const getJurisdictionColor = (level) => {
    const colors = {
      'federal': 'bg-blue-100 text-blue-800',
      'state': 'bg-green-100 text-green-800',
      'county': 'bg-purple-100 text-purple-800',
      'city': 'bg-orange-100 text-orange-800',
      'municipal': 'bg-orange-100 text-orange-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const fetchLaborLaws = async (showLoading = false) => {
    if (!company?.id) return;
    try {
      if (showLoading) setIsLoading(true);
      const response = await getRightworkAPI(API_list_labor_laws_for_company, { company_id: company.id });
      setStores(response?.data?.redux?.payload?.stores || []);
    } catch (error) {
      console.error("Error fetching labor laws:", error);
      toast.error("Failed to fetch labor law data");
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const fetchJurisdictions = async () => {
    if (!company?.id) return;
    try {
      const response = await getRightworkAPI(API_list_jurisdictions, { company_id: company.id });
      const payload = response?.data?.redux?.payload;
      if (payload) {
        setJurisdictionOptions({
          federal: payload.federal || [],
          states: payload.states || [],
          counties: payload.counties || [],
          municipalities: payload.municipalities || [],
        });
      }
    } catch (error) {
      console.error("Error fetching jurisdictions:", error);
    }
  };

  React.useEffect(() => {
    fetchLaborLaws(true);
    fetchJurisdictions();
  }, [company?.id]);

  // Refetch when the Operations Assistant changes a store's jurisdiction.
  // Seeded at mount so a stale signal does not refire on navigation.
  const opsRefreshTimestamp = props.opsRefreshSignal?.timestamp;
  const lastOpsRefreshRef = React.useRef(opsRefreshTimestamp);
  React.useEffect(() => {
    if (!opsRefreshTimestamp || opsRefreshTimestamp === lastOpsRefreshRef.current) {
      return;
    }
    lastOpsRefreshRef.current = opsRefreshTimestamp;
    if (!company?.id || !props.opsRefreshSignal) return;
    const types = props.opsRefreshSignal.types || [];
    if (types.includes("labor_laws")) {
      fetchLaborLaws(false);
      fetchJurisdictions();
    }
  }, [opsRefreshTimestamp]);

  const getJurisdictionName = (law, storeJurisdiction) => {
    const level = law.jurisdiction_level;
    if (level === 'federal' && storeJurisdiction.federal) {
      const name = storeJurisdiction.federal === 'United States of America' ? 'United States' : storeJurisdiction.federal;
      return `${name} Law`;
    } else if (level === 'state' && storeJurisdiction.state) {
      return `${storeJurisdiction.state} Law`;
    } else if (level === 'county' && storeJurisdiction.county) {
      return `${storeJurisdiction.county} Law`;
    } else if ((level === 'municipal' || level === 'city') && (storeJurisdiction.municipal || storeJurisdiction.city)) {
      return `${storeJurisdiction.municipal || storeJurisdiction.city} Law`;
    }
    const levelNames = { 'federal': 'Federal Law', 'state': 'State Law', 'county': 'County Law', 'city': 'City/Municipal Law', 'municipal': 'Municipal Law' };
    return levelNames[level] || level;
  };

  const transformedStores = React.useMemo(() => {
    if (!stores || stores.length === 0) return [];
    return stores.filter((store) => !props.settingsStoreId || Number(store.id) === Number(props.settingsStoreId)).map(store => {
      const regulationTypeOrder = [
        'weekly_overtime', 'daily_overtime', 'seventh_day_overtime',
        'meal_break', 'meal_break_timing', 'rest_break', 'rest_break_timing', 'shift_separation',
        'advance_schedule_notice', 'schedule_change_penalty',
        'minor_work_hours_14_15', 'minor_work_hours_16_17',
        'split_shift', 'predictive_scheduling', 'teen_hours_restriction'
      ];
      const jurisdictionLevelOrder = ['federal', 'state', 'county', 'municipal'];
      const sortByType = (a, b) => {
        const aJIdx = jurisdictionLevelOrder.indexOf(a.jurisdiction_level);
        const bJIdx = jurisdictionLevelOrder.indexOf(b.jurisdiction_level);
        const jDiff = (aJIdx === -1 ? 999 : aJIdx) - (bJIdx === -1 ? 999 : bJIdx);
        if (jDiff !== 0) return jDiff;
        const aIdx = regulationTypeOrder.indexOf(a.regulation_type);
        const bIdx = regulationTypeOrder.indexOf(b.regulation_type);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      };
      const allRegulations = [...store.labor_laws].sort(sortByType);
      const mapRegulation = (law) => ({
        id: law.uuid,
        uuid: law.uuid,
        regulationType: law.regulation_type,
        jurisdictionLevel: law.jurisdiction_level,
        jurisdictionName: getJurisdictionName(law, store.jurisdiction),
        legalCitation: law.legal_citation,
        legalAuthorityType: law.legal_authority_type,
        effectiveDate: law.effective_date,
        isActive: law.is_active,
        isHidden: law.is_hidden,
        parameters: law.parameters,
        description: law.legal_description
      });
      return {
        id: store.id,
        name: store.store_name,
        jurisdiction: store.jurisdiction || {},
        jurisdiction_ids: store.jurisdiction_ids || {},
        regulations: allRegulations.map(mapRegulation),
      };
    });
  }, [stores, props.settingsStoreId]);

  // Setup Assistant follow-along: open the location under review and
  // bring it into view once its card is rendered.
  const focusStoreId = props.focusStore?.storeId;
  const focusAt = props.focusStore?.at;
  // One expand + pulse per focus request: the store list refetches after
  // every toggle on this page, which must not replay the highlight.
  const handledFocusRef = React.useRef(null);
  React.useEffect(() => {
    if (!focusStoreId || handledFocusRef.current === focusAt) return;
    if (!stores.some((store) => Number(store.id) === Number(focusStoreId))) return;
    handledFocusRef.current = focusAt;
    setExpandedStores((prev) => (prev[focusStoreId] ? prev : { ...prev, [focusStoreId]: true }));
    const frame = window.requestAnimationFrame(() => {
      const el = document.getElementById(laborLawsStoreCardId(focusStoreId));
      if (el) pulseElement(el);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusStoreId, focusAt, stores]);

  const toggleStore = (storeId) => {
    setExpandedStores(prev => ({ ...prev, [storeId]: !prev[storeId] }));
  };

  const toggleRegulation = (regulationId) => {
    setExpandedRegulations(prev => ({ ...prev, [regulationId]: !prev[regulationId] }));
  };


  const activateRegulation = async (storeId, regulationUuid) => {
    try {
      toast.loading("Activating regulation...", { autoClose: false });
      const response = await postRightworkAPI(API_set_labor_law_hidden_for_store, {
        store_id: storeId,
        company_id: company.id,
        labor_law_regulation_uuid: regulationUuid,
        is_hidden: false
      });
      if (response.status === 200) {
        toast.dismiss();
        toast.success("Regulation activated successfully!", { autoClose: 3000 });
        fetchLaborLaws();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message || "Failed to activate regulation", { autoClose: 3000 });
    }
  };

  const deactivateRegulation = async (storeId, regulationUuid) => {
    try {
      toast.loading("Deactivating regulation...", { autoClose: false });
      const response = await postRightworkAPI(API_set_labor_law_hidden_for_store, {
        store_id: storeId,
        company_id: company.id,
        labor_law_regulation_uuid: regulationUuid,
        is_hidden: true
      });
      if (response.status === 200) {
        toast.dismiss();
        toast.success("Regulation deactivated successfully!", { autoClose: 3000 });
        fetchLaborLaws();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message || "Failed to deactivate regulation", { autoClose: 3000 });
    }
  };

  // --- Drawer logic ---

  const handleOpenDrawer = (store) => {
    setDrawerStore(store);
    // Find the matching store from raw API data to get IDs
    const rawStore = stores.find(s => s.id === store.id);
    setSelectedJurisdictions({
      federal_id: rawStore?.jurisdiction_ids?.federal_id ?? "",
      state_id: rawStore?.jurisdiction_ids?.state_id ?? "",
      county_id: rawStore?.jurisdiction_ids?.county_id ?? "",
      municipal_id: rawStore?.jurisdiction_ids?.municipal_id ?? "",
    });
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerStore(null);
  };

  const handleJurisdictionChange = (level, value) => {
    setSelectedJurisdictions(prev => {
      const updated = { ...prev, [level]: value };
      // Clear more granular jurisdictions when a less granular one changes
      if (level === "federal_id") {
        updated.state_id = "";
        updated.county_id = "";
        updated.municipal_id = "";
      } else if (level === "state_id") {
        updated.county_id = "";
        updated.municipal_id = "";
      } else if (level === "county_id") {
        updated.municipal_id = "";
      }
      return updated;
    });
  };

  const handleSaveJurisdictions = async () => {
    if (!drawerStore) return;
    setSaving(true);
    try {
      const response = await postRightworkAPI(API_update_store_jurisdictions, {
        store_id: drawerStore.id,
        company_id: company.id,
        jurisdiction_federal_id: selectedJurisdictions.federal_id || null,
        jurisdiction_state_id: selectedJurisdictions.state_id || null,
        jurisdiction_county_id: selectedJurisdictions.county_id || null,
        jurisdiction_municipal_id: selectedJurisdictions.municipal_id || null,
      });
      if (response.status === 200) {
        toast.success("Jurisdictions updated successfully!", { autoClose: 3000 });
        handleCloseDrawer();
        fetchLaborLaws();
      }
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Failed to update jurisdictions", { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  // Filtered options based on current selections
  const filteredStates = selectedJurisdictions.federal_id
    ? jurisdictionOptions.states.filter(s => s.federal_id === selectedJurisdictions.federal_id)
    : jurisdictionOptions.states;

  const filteredCounties = selectedJurisdictions.state_id
    ? jurisdictionOptions.counties.filter(c => c.state_id === selectedJurisdictions.state_id)
    : jurisdictionOptions.counties;

  const filteredMunicipalities = React.useMemo(() => {
    let opts = jurisdictionOptions.municipalities;
    if (selectedJurisdictions.county_id) {
      opts = opts.filter(m => m.county_id === selectedJurisdictions.county_id);
    } else if (selectedJurisdictions.state_id) {
      opts = opts.filter(m => m.state_id === selectedJurisdictions.state_id);
    }
    return opts;
  }, [jurisdictionOptions.municipalities, selectedJurisdictions.state_id, selectedJurisdictions.county_id]);

  const selectSx = {
    backgroundColor: "white",
    borderRadius: "8px",
    height: "45px",
    "& .MuiSelect-select": {
      padding: "10px 16px",
      minHeight: "unset",
      display: "flex",
      alignItems: "center",
      height: "45px",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  };

  const renderRegulationRow = (regulation, storeId) => {
    const regKey = `${storeId}-${regulation.id}`;
    const isExpanded = expandedRegulations[regKey];
    const isMonitored = !regulation.isHidden;

    return (
      <div key={regulation.id}>
        <div
          className="py-3 px-4 cursor-pointer hover:bg-slate-50 transition-colors border-t border-slate-200"
          onClick={() => toggleRegulation(regKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${getJurisdictionColor(regulation.jurisdictionLevel)}`}>
                {regulation.jurisdictionLevel.toUpperCase()}
              </span>
              <div className="min-w-0">
                <span className={`text-sm font-medium ${isMonitored ? "text-slate-800" : "text-slate-400"}`}>
                  {getRegulationTypeLabel(regulation.regulationType)}
                </span>
                <span className={`block text-xs mt-1 ${isMonitored ? "text-slate-500" : "text-slate-400"}`}>
                  {regulation.jurisdictionName}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3 shrink-0 ml-4">
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isMonitored ? "text-emerald-600" : "text-slate-400"
                }`}
              >
                {isMonitored ? "On" : "Off"}
              </span>
              <PurpleSwitch
                checked={isMonitored}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMonitored) {
                    deactivateRegulation(storeId, regulation.uuid);
                  } else {
                    activateRegulation(storeId, regulation.uuid);
                  }
                }}
              />
              {isExpanded ? (
                <ExpandLessIcon className="text-slate-400" style={{ fontSize: "20px" }} />
              ) : (
                <ExpandMoreIcon className="text-slate-400" style={{ fontSize: "20px" }} />
              )}
            </div>
          </div>
        </div>

        <Collapse in={isExpanded}>
          <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
            <div className="pt-3">
              <p className="text-sm text-slate-600 mb-3">{regulation.description}</p>
              <div className="bg-white rounded-lg border border-slate-200 p-3 mb-3">
                <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Compliance Parameters</h5>
                <pre className="text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(regulation.parameters, null, 2)}
                </pre>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
                <div>
                  <span className="font-medium text-slate-700">Legal Authority:</span>{" "}
                  <span className="capitalize">{regulation.legalAuthorityType}</span>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Citation:</span>{" "}
                  {regulation.legalCitation}
                </div>
                <div>
                  <span className="font-medium text-slate-700">Effective:</span>{" "}
                  {new Date(regulation.effectiveDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full" style={{ minHeight: props.settingsStoreId ? 160 : 'calc(100vh - 100px)' }}>
        <LoadSpinner />
      </div>
    );
  }

  return (
    <div className={props.settingsStoreId ? "py-4" : "p-6"}>
      <div className="mb-6">
        {!props.settingsStoreId && <h2 className="text-2xl font-bold text-slate-800 mb-1">Labor Law Regulations</h2>}
        <p className="text-slate-500 text-sm">{props.settingsStoreId ? "Jurisdictions and regulations monitored for this location." : "View and manage labor law regulations monitored for compliance across all store locations"}</p>
      </div>

      <div className="mb-6">
        <Tooltip
          title="Labor law tracking provided by Rightwork is for informational purposes only and does not constitute legal advice. Rightwork is not a payroll provider and is not liable for any labor law violations or compliance failures. It is your responsibility to ensure full compliance with all applicable federal, state, and local labor laws. We recommend consulting with a qualified legal or HR professional for guidance specific to your business."
          arrow
          placement="bottom-start"
          slotProps={{
            tooltip: {
              sx: { maxWidth: 400, fontSize: "12px", lineHeight: 1.5, padding: "12px 16px" },
            },
          }}
        >
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 cursor-help inline-flex items-center gap-1 font-medium">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            Legal Disclaimer
          </span>
        </Tooltip>
      </div>

      <div className="space-y-3">
        {transformedStores.map((store) => (
          <div key={store.id} id={laborLawsStoreCardId(store.id)} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            {/* Location summary and jurisdiction details */}
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-base break-words">{store.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {store.regulations.filter(r => !r.isHidden).length} active regulation{store.regulations.filter(r => !r.isHidden).length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 transition-colors"
                    onClick={() => handleOpenDrawer(store)}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                    Edit jurisdictions
                  </button>
                  {!props.settingsStoreId && (
                    <button
                      type="button"
                      aria-label={`${expandedStores[store.id] ? "Collapse" : "Expand"} regulations for ${store.name}`}
                      aria-expanded={Boolean(expandedStores[store.id])}
                      className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 p-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
                      onClick={() => toggleStore(store.id)}
                    >
                      {expandedStores[store.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </button>
                  )}
                </div>
              </div>
              {!store.jurisdiction.federal && !store.jurisdiction.state && !store.jurisdiction.county && !store.jurisdiction.municipal && !store.jurisdiction.city ? (
                <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-500">No jurisdictions set</p>
              ) : (
                <dl
                  className="grid gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))" }}
                >
                  {[
                    ["Federal", store.jurisdiction.federal],
                    ["State", store.jurisdiction.state],
                    ["County", store.jurisdiction.county],
                    ["City", store.jurisdiction.municipal || store.jurisdiction.city],
                  ].filter(([, name]) => name).map(([level, name]) => (
                    <div key={level} className="min-w-0">
                      <dt className="text-xs font-medium text-slate-500">{level}</dt>
                      <dd className="mt-1 text-sm font-medium text-slate-800 break-words">{name}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            {/* Expanded store content */}
            <Collapse in={Boolean(props.settingsStoreId) || expandedStores[store.id]}>
              <div className="border-t border-slate-200 bg-white">
                {store.regulations.length > 0 ? (
                  <div>
                    {store.regulations.map((regulation) =>
                      renderRegulationRow(regulation, store.id)
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-slate-500">
                    No labor law regulations found for this store's jurisdiction.
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        ))}
      </div>

      {transformedStores.length === 0 && !isLoading && (
        <div className="text-center py-12 text-slate-500">
          No stores found for this company.
        </div>
      )}

      {/* Jurisdiction Edit Drawer */}
      <Drawer open={drawerOpen} anchor="right" onClose={handleCloseDrawer}>
        <div className="w-[450px] max-w-[100vw] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={`Edit Jurisdictions`}
              onClose={handleCloseDrawer}
            />
            <div className="w-full px-6">
              <div className="mt-2 mb-6">
                <p className="text-slate-500 text-sm">
                  Select the jurisdictions that apply to <span className="font-semibold text-slate-900">{drawerStore?.name}</span>. Applicable labor law regulations will automatically update based on the selected jurisdictions.
                </p>
              </div>

              {/* Federal */}
              <div className="mt-5">
                <p className="text-sm text-slate-500 mb-1">Federal</p>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedJurisdictions.federal_id}
                  onChange={(e) => handleJurisdictionChange("federal_id", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <span className="text-slate-400">None</span>
                  </MenuItem>
                  {jurisdictionOptions.federal.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* State */}
              <div className="mt-5">
                <p className="text-sm text-slate-500 mb-1">State</p>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedJurisdictions.state_id}
                  onChange={(e) => handleJurisdictionChange("state_id", e.target.value)}
                  disabled={!selectedJurisdictions.federal_id}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <span className="text-slate-400">None</span>
                  </MenuItem>
                  {filteredStates.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* County */}
              <div className="mt-5">
                <p className="text-sm text-slate-500 mb-1">County</p>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedJurisdictions.county_id}
                  onChange={(e) => handleJurisdictionChange("county_id", e.target.value)}
                  disabled={!selectedJurisdictions.state_id}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <span className="text-slate-400">None</span>
                  </MenuItem>
                  {filteredCounties.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Municipal */}
              <div className="mt-5">
                <p className="text-sm text-slate-500 mb-1">City / Municipality</p>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedJurisdictions.municipal_id}
                  onChange={(e) => handleJurisdictionChange("municipal_id", e.target.value)}
                  disabled={!selectedJurisdictions.county_id}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <span className="text-slate-400">None</span>
                  </MenuItem>
                  {filteredMunicipalities.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
            <div className="h-[200px]" />
          </div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton
                variant="contained"
                loading={saving}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                onClick={handleSaveJurisdictions}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  fontSize: "16px",
                }}
              >
                Save Jurisdictions
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              <ButtonOutlined text="Cancel" onClick={handleCloseDrawer} />
            </div>
          </DrawerFooterContainerLarge>
        </div>
      </Drawer>
    </div>
  );
}

export default LaborLawsDisplay;
